import { Fragment, useContext, useEffect, useMemo, useState } from "react";
import { MobXProviderContext } from "mobx-react";
import { useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { confirmDialog } from "primereact/confirmdialog";
import { Dropdown } from "primereact/dropdown";
import { FileUpload } from "primereact/fileupload";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { MultiSelect } from "primereact/multiselect";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import { TabPanel, TabView } from "primereact/tabview";
import { useRef } from "react";
import { appConfirmDialogClasses, appDataTableWrapperStyle, appInnerCardStyle, appMutedTextStyle, renderConfirmMessage, renderSurfaceHeader } from "../../../components/general/uiSurfaceStyles";
import { ReferenceType } from "../../../models/document";
import { useResponsive } from "../../../responsive/ResponsiveProvider";
import { useAuthExperience } from "../../auth/hooks/useAuthExperience";
import { combineLosAngelesDateAndTime } from "../../../utils/losAngelesDateTime";
import AdminResetControl from "../../../components/general/AdminResetControl";
import { StaffFacilityTasksPage } from "../../shared/pages/SharedFacilityTasksPage";

const TIMEZONE = "America/Los_Angeles";
const todayKey = () => new Intl.DateTimeFormat("en-CA", { timeZone: TIMEZONE, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
const dateKeyFromDate = (date: Date) => new Intl.DateTimeFormat("en-CA", { timeZone: TIMEZONE, year: "numeric", month: "2-digit", day: "2-digit" }).format(date);
const DAYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"].map((value) => ({ label: value.toUpperCase(), value }));
const EMPTY_TASK = { title: "", instructions: "", scope: "facility", residentId: "", category: "Other", priority: "normal", status: "pending", assignedStaffIds: [], scheduleType: "daily", scheduledDays: [], scheduleMonthDays: [], scheduledTimes: [], startDate: todayKey(), endDate: "" };
const IMPORT_EXTENSIONS = new Set(["xls", "xlsx", "docx", "txt"]);
const MAX_IMPORT_FILE_SIZE = 1024 * 1024;
const TASK_CATEGORY_DEFAULTS = ["Meals", "Kitchen", "Cleaning", "Resident Care", "Medication", "Safety", "Documentation", "Activities", "Appointments", "Supplies", "Laundry", "Maintenance", "Other"];
const TASK_TIME_OPTIONS = Array.from({ length: 96 }, (_, index) => {
  const hours = Math.floor(index / 4);
  const minutes = (index % 4) * 15;
  const value = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  const suffix = hours >= 12 ? "PM" : "AM";
  const hour12 = hours % 12 || 12;
  return { label: `${hour12}:${String(minutes).padStart(2, "0")} ${suffix}`, value };
});
const SCHEDULE_MONTH_DAY_OPTIONS = Array.from({ length: 31 }, (_, index) => ({ label: String(index + 1), value: index + 1 }));
const FACILITY_TASK_ENTRY_STATUS_OPTIONS = [
  { label: "Completed", value: "completed" },
  { label: "Not Completed", value: "not_completed" },
  { label: "Skipped", value: "skipped" },
];
const facilityTaskStatusLabel = (value: any) => String(value || "").toLowerCase() === "not_charted"
  ? "Not Charted"
  : FACILITY_TASK_ENTRY_STATUS_OPTIONS.find((option) => option.value === String(value || "").toLowerCase())?.label || "Pending";
const facilityTaskStatusSeverity = (value: any): "success" | "danger" | "warning" | "info" => {
  const status = String(value || "").toLowerCase();
  if (status === "completed") return "success";
  if (status === "not_completed") return "danger";
  if (status === "skipped" || status === "not_charted") return "warning";
  return "info";
};
const formatTaskDateTime = (dateValue: any, timeValue?: any) => {
  const date = String(dateValue || "").trim();
  const time = String(timeValue || "").trim();
  if (!date) return "—";
  if (!/^\d{2}:\d{2}$/.test(time)) return date;
  const [hours, minutes] = time.split(":").map(Number);
  const suffix = hours >= 12 ? "PM" : "AM";
  const hour12 = hours % 12 || 12;
  return `${date} ${hour12}:${String(minutes).padStart(2, "0")} ${suffix}`;
};
const formatTaskLastUpdated = (value: any) => {
  const date = new Date(String(value || ""));
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString("en-US", { timeZone: TIMEZONE, dateStyle: "medium", timeStyle: "short" });
};
const getWeekStartKey = (dateValue: string) => {
  const date = new Date(`${dateValue}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() - ((date.getUTCDay() + 6) % 7));
  return new Intl.DateTimeFormat("en-CA", { timeZone: TIMEZONE, year: "numeric", month: "2-digit", day: "2-digit" }).format(date);
};
const escapeFacilityTaskExportHtml = (value: any) => String(value || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
const canEditFacilityTask = (row: any) => String(row?.occurrenceDate || row?.startDate || "9999-12-31") <= todayKey();
const taskDateKey = (row: any) => String(row?.occurrenceDate || row?.startDate || "9999-12-31").slice(0, 10);
const addLosAngelesDays = (dateKey: string, days: number) => {
  const date = new Date(`${dateKey}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return new Intl.DateTimeFormat("en-CA", { timeZone: TIMEZONE, year: "numeric", month: "2-digit", day: "2-digit" }).format(date);
};
const getFutureReminderDateKey = (row: any, timeKey: string) => {
  const now = new Date();
  const today = todayKey();
  const selectedDays = Array.isArray(row?.scheduledDays) ? row.scheduledDays.map((day: any) => String(day).toLowerCase()) : [];
  const selectedMonthDays = Array.isArray(row?.scheduleMonthDays) ? row.scheduleMonthDays.map(Number).filter((day: number) => Number.isInteger(day) && day >= 1 && day <= 31) : [];
  const scheduleType = String(row?.scheduleType || "daily");
  const candidates = scheduleType === "one_time" && row?.startDate
    ? [String(row.startDate).slice(0, 10)]
    : Array.from({ length: scheduleType === "monthly" ? 370 : selectedDays.length ? 14 : 2 }, (_, index) => addLosAngelesDays(today, index));

  for (const candidate of candidates) {
    if (selectedDays.length && !selectedDays.includes(new Intl.DateTimeFormat("en-US", { timeZone: TIMEZONE, weekday: "short" }).format(new Date(`${candidate}T12:00:00Z`)).slice(0, 3).toLowerCase())) continue;
    if (scheduleType === "monthly" && selectedMonthDays.length && !selectedMonthDays.includes(Number(candidate.slice(-2)))) continue;
    const startAt = combineLosAngelesDateAndTime(candidate, timeKey);
    if (startAt && new Date(startAt).getTime() > now.getTime()) return candidate;
  }

  return addLosAngelesDays(today, 1);
};
const formatTaskDateGroup = (dateKey: string) => new Date(`${dateKey}T12:00:00Z`).toLocaleDateString("en-US", { timeZone: TIMEZONE, weekday: "long", month: "long", day: "numeric", year: "numeric" });

const FacilityTasksEmptyState = () => (
  <>
    <div className="flex flex-column align-items-center text-center">
      <div className="font-semibold text-2xl text-900">Set up facility tasks</div>
      <div className="text-color-secondary mt-2" style={{ maxWidth: "44rem" }}>
        No facility tasks have been scheduled for this home yet. Add routines for the team, and use this page to keep assignments and completion status organized in one place.
      </div>
    </div>
    <div className="mt-4 text-center">
      <div className="font-semibold text-900">You can use Facility Tasks to:</div>
      <ul className="mt-2 mb-0 pl-5 text-color-secondary" style={{ display: "inline-block", maxWidth: "100%", lineHeight: 1.8, textAlign: "left" }}>
        <li>Create facility-wide or resident-specific tasks.</li>
        <li>Assign tasks to staff and schedule recurring times.</li>
        <li>Track pending, completed, skipped, and not completed work.</li>
        <li>Review activity across the home from one shared task list.</li>
      </ul>
    </div>
    <div className="flex align-items-start justify-content-center gap-2 mt-3 text-color-secondary text-sm text-center">
      <i className="pi pi-info-circle mt-1" aria-hidden="true" />
      <span>Add your first task using the task actions above, then assign it to the appropriate staff member.</span>
    </div>
  </>
);

const FacilityTasksPage = ({ sharedMode = false }: { sharedMode?: boolean }) => {
  const { homeId } = useParams();
  const { backendAPIStore, authStore } = useContext(MobXProviderContext);
  const mobile = useResponsive().isMobile;
  const toast = useRef<Toast>(null);
  const authExperience = useAuthExperience();
  const [tasks, setTasks] = useState<any[]>([]);
  const [residents, setResidents] = useState<any[]>([]);
  const [staff, setStaff] = useState<any[]>([]);
  const [dateKey, setDateKey] = useState(todayKey());
  const [taskView, setTaskView] = useState<"upcoming" | "previous">("upcoming");
  const [taskState, setTaskState] = useState<"active" | "deleted">("active");
  const [taskStateCounts, setTaskStateCounts] = useState({ active: 0, deleted: 0 });
  const [taskCursor, setTaskCursor] = useState<string | null>(null);
  const [hasMoreTasks, setHasMoreTasks] = useState(false);
  const [hasPreviousTasks, setHasPreviousTasks] = useState(false);
  const [loadingMoreTasks, setLoadingMoreTasks] = useState(false);
  const [taskSortField, setTaskSortField] = useState("time");
  const [taskSortOrder, setTaskSortOrder] = useState<1 | -1>(1);
  const [searchText, setSearchText] = useState("");
  const [assigneeFilter, setAssigneeFilter] = useState("");
  const [customCategories, setCustomCategories] = useState<Array<{ categoryId: string; label: string }>>([]);
  const [categoryDialogVisible, setCategoryDialogVisible] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [editingCategoryId, setEditingCategoryId] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [task, setTask] = useState<any>(EMPTY_TASK);
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [taskNotes, setTaskNotes] = useState<Record<string, string>>({});
  const [importDialogVisible, setImportDialogVisible] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importJob, setImportJob] = useState<any>(null);
  const [importRows, setImportRows] = useState<any[]>([]);
  const [importPhase, setImportPhase] = useState<"idle" | "uploading" | "processing" | "completed" | "failed">("idle");
  const [importStartedAt, setImportStartedAt] = useState<number | null>(null);
  const [importElapsedSeconds, setImportElapsedSeconds] = useState(0);
  const [exportDialogVisible, setExportDialogVisible] = useState(false);
  const [exportStaffId, setExportStaffId] = useState("");
  const [exportResidentId, setExportResidentId] = useState("");
  const [exportWeekStart, setExportWeekStart] = useState(getWeekStartKey(todayKey()));
  const [exportView, setExportView] = useState<"list" | "grid">("grid");
  const [exporting, setExporting] = useState(false);
  const [exportDataset, setExportDataset] = useState<any>(null);
  const [exportDocumentUrl, setExportDocumentUrl] = useState("");
  const exportPreviewFrameRef = useRef<HTMLIFrameElement | null>(null);
  const [templateDialogVisible, setTemplateDialogVisible] = useState(false);
  const [templateRows, setTemplateRows] = useState<any[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [savingTemplates, setSavingTemplates] = useState(false);
  const [calendarSavingTaskId, setCalendarSavingTaskId] = useState("");
  const [pastEntryDateKey, setPastEntryDateKey] = useState(todayKey());
  const [pastEntries, setPastEntries] = useState<any[]>([]);
  const [pastEntriesLoading, setPastEntriesLoading] = useState(false);
  const [correctionEntry, setCorrectionEntry] = useState<any>(null);
  const [correctionStatus, setCorrectionStatus] = useState("");
  const [correctionNote, setCorrectionNote] = useState("");
  const [correctionSaving, setCorrectionSaving] = useState(false);
  const [historyEntry, setHistoryEntry] = useState<any>(null);
  const [entryHistory, setEntryHistory] = useState<any[]>([]);
  const [historyCurrentEntry, setHistoryCurrentEntry] = useState<any>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const isProvider = authStore.getUserRoleType() === "provider" || authStore.isAdmin();
  const hasFullTaskAccess = isProvider || sharedMode;
  const resetTasks = async () => {
    if (!homeId) return;
    await backendAPIStore.resetFacilityTasks(homeId);
    setTasks([]);
    toast.current?.show({ severity: "success", summary: "Facility tasks reset", detail: "All tasks and task history for this home were deleted." });
  };

  const load = async (append = false) => {
    if (!homeId) return;
    if (append) setLoadingMoreTasks(true); else setLoading(true);
    try {
      await backendAPIStore.init();
      const response = await backendAPIStore.getFacilityTasks(homeId, {
        dateKey,
        assignedToMe: !hasFullTaskAccess,
        view: taskView,
        taskState,
        cursor: append ? taskCursor : null,
        pageSize: 100,
        sortField: taskSortField,
        sortOrder: taskSortOrder === -1 ? "desc" : "asc",
      });
      const nextTasks = Array.isArray(response?.tasks) ? response.tasks : [];
      setTasks((current) => {
        const nextTaskList = append ? [...current, ...nextTasks] : nextTasks;
        window.dispatchEvent(new CustomEvent("facility-tasks:availability", { detail: { count: nextTaskList.length } }));
        return nextTaskList;
      });
      setTaskCursor(response?.nextCursor || null);
      setHasMoreTasks(Boolean(response?.hasMore));
      setHasPreviousTasks(Boolean(response?.previousToken));
      if (response?.taskStateCounts) setTaskStateCounts({ active: Number(response.taskStateCounts.active || 0), deleted: Number(response.taskStateCounts.deleted || 0) });
      if (Array.isArray(response?.categories)) setCustomCategories(response.categories.map((category: any) => ({ categoryId: String(category?.categoryId || ""), label: String(category?.label || "").trim() })).filter((category: any) => category.label));
      if (hasFullTaskAccess) {
        const [residentResponse, staffResponse] = await Promise.all([
          backendAPIStore.getResidentsForHome(homeId),
          backendAPIStore.getLinkedCaregiversForHome(homeId),
        ]);
        setResidents(Array.isArray(residentResponse) ? residentResponse : Array.isArray(residentResponse?.items) ? residentResponse.items : []);
        const linkedStaff = Array.isArray(staffResponse)
          ? staffResponse
          : Array.isArray(staffResponse?.items)
            ? staffResponse.items
            : Array.isArray(staffResponse?.caregivers)
              ? staffResponse.caregivers
              : [];
        setStaff(linkedStaff.filter((item: any) => item?.active !== false && item?.archived !== true));
      }
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: "Facility Tasks", detail: error?.message || "Unable to load tasks." });
    } finally { setLoading(false); setLoadingMoreTasks(false); }
  };

  useEffect(() => { setTaskCursor(null); void load(false); }, [homeId, dateKey, hasFullTaskAccess, taskView, taskState, taskSortField, taskSortOrder]);

  const loadPastEntries = async () => {
    if (!homeId || !isProvider) return;
    setPastEntriesLoading(true);
    try {
      const response = await backendAPIStore.getFacilityTaskPastEntries(homeId, pastEntryDateKey);
      setPastEntries(Array.isArray(response?.entries) ? response.entries : []);
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: "Past Entries", detail: error?.message || "Unable to load past entries." });
    } finally {
      setPastEntriesLoading(false);
    }
  };

  useEffect(() => {
    if (isProvider && activeTabIndex === 2) void loadPastEntries();
  }, [homeId, isProvider, activeTabIndex, pastEntryDateKey]);

  const openCorrectionDialog = (entry: any) => {
    setCorrectionEntry(entry);
    setCorrectionStatus(entry?.isCharted ? String(entry?.status || "") : "");
    setCorrectionNote("");
  };

  const savePastEntryCorrection = async () => {
    if (!homeId || !correctionEntry || !correctionStatus || !correctionNote.trim()) return;
    setCorrectionSaving(true);
    try {
      await backendAPIStore.correctFacilityTaskEntry({ homeId, taskId: correctionEntry.taskId, dateKey: pastEntryDateKey, status: correctionStatus, note: correctionNote.trim() });
      setCorrectionEntry(null);
      setCorrectionNote("");
      await loadPastEntries();
      toast.current?.show({ severity: "success", summary: correctionEntry?.isCharted ? "Entry corrected" : "Entry charted", detail: correctionEntry?.isCharted ? "The chart entry and its audit history were updated." : "The past task entry was charted and added to audit history." });
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: "Correction failed", detail: error?.message || "Unable to correct the entry." });
    } finally {
      setCorrectionSaving(false);
    }
  };

  const openEntryHistory = async (entry: any) => {
    if (!homeId) return;
    setHistoryEntry(entry);
    setEntryHistory([]);
    setHistoryCurrentEntry(null);
    setHistoryLoading(true);
    try {
      const response = await backendAPIStore.getFacilityTaskEntryHistory(homeId, entry.taskId, pastEntryDateKey);
      setEntryHistory(Array.isArray(response?.history) ? response.history : []);
      setHistoryCurrentEntry(response?.entry || null);
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: "Audit History", detail: error?.message || "Unable to load audit history." });
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (!importing || !importStartedAt) return undefined;
    const intervalId = window.setInterval(() => setImportElapsedSeconds(Math.floor((Date.now() - importStartedAt) / 1000)), 1000);
    return () => window.clearInterval(intervalId);
  }, [importing, importStartedAt]);

  const residentOptions = useMemo(() => residents.filter((resident: any) => resident?.active !== false && resident?.archived !== true).map((resident: any) => ({ label: resident?.residentDisplayName || `${resident?.firstName || ""} ${resident?.lastName || ""}`.trim() || "Resident", value: resident?.residentId })), [residents]);
  const residentFilterOptions = useMemo(() => [{ label: "All residents", value: "" }, ...residentOptions], [residentOptions]);
  const staffOptions = useMemo(() => staff.filter((person: any) => person?.active !== false && person?.archived !== true).map((person: any) => ({
    label: `${person?.firstName || ""} ${person?.lastName || ""}`.trim() || person?.email || "Staff",
    value: person?.caregiverId || person?.userId || person?.staffId,
  })), [staff]);
  const assigneeFilterOptions = useMemo(() => [{ label: "All assignees", value: "" }, ...staffOptions], [staffOptions]);
  const getAssigneeLabel = (row: any) => {
    if (Array.isArray(row?.assignedStaffNames) && row.assignedStaffNames.length) {
      return row.assignedStaffNames.join(", ");
    }
    const assignedIds = Array.isArray(row?.assignedStaffIds) ? row.assignedStaffIds : (row?.assignedStaffId ? [row.assignedStaffId] : []);
    const labels = assignedIds.map((id: string) => {
      const normalizedId = String(id || "").trim();
      const person = staff.find((candidate: any) => [candidate?.caregiverId, candidate?.userId, candidate?.staffId]
        .map((value: any) => String(value || "").trim())
        .includes(normalizedId));
      return person
        ? `${person?.firstName || ""} ${person?.lastName || ""}`.trim() || person?.email || "Staff"
        : staffOptions.find((option) => String(option.value || "").trim() === normalizedId)?.label || normalizedId;
    }).filter(Boolean);
    return labels.length ? labels.join(", ") : "Unassigned";
  };
  const openAddTaskDialog = () => {
    setEditingTaskId(null);
    setTask({ ...EMPTY_TASK, assignedStaffIds: staffOptions[0]?.value ? [staffOptions[0].value] : [], startDate: todayKey() });
    setDialogVisible(true);
  };
  const openCopyTask = (row: any) => {
    setEditingTaskId(null);
    setTask({
      ...EMPTY_TASK,
      title: String(row?.title || ""),
      instructions: String(row?.instructions || ""),
      scope: row?.scope || "facility",
      residentId: String(row?.residentId || ""),
      category: row?.category || "Other",
      priority: row?.priority || "normal",
      status: "pending",
      assignedStaffIds: Array.isArray(row?.assignedStaffIds) && row.assignedStaffIds.length
        ? [row.assignedStaffIds[0]]
        : (row?.assignedStaffId ? [row.assignedStaffId] : []),
      scheduleType: row?.scheduleType || "daily",
      scheduledDays: Array.isArray(row?.scheduledDays) ? [...row.scheduledDays] : [],
      scheduledTimes: Array.isArray(row?.scheduledTimes) ? [...row.scheduledTimes] : [],
      startDate: row?.startDate || todayKey(),
      endDate: row?.endDate || "",
    });
    setDialogVisible(true);
  };
  const openExportDialog = () => {
    setExportStaffId(staffOptions[0]?.value || "");
    setExportResidentId("");
    setExportWeekStart(getWeekStartKey(dateKey));
    setExportView("grid");
    setExportDataset(null);
    setExportDocumentUrl("");
    setExportDialogVisible(true);
  };
  const openAutoGenerateDialog = async () => {
    if (!homeId || !hasFullTaskAccess) return;
    setLoadingTemplates(true);
    try {
      const response = await backendAPIStore.getFacilityTaskTemplates(homeId);
      const rows = Array.isArray(response?.templates) ? response.templates : [];
      setTemplateRows(rows.map((item: any) => ({
        ...item,
        selected: true,
        scope: "facility",
        residentId: "",
        priority: "normal",
        assignedStaffIds: staffOptions[0]?.value ? [staffOptions[0].value] : [],
        startDate: todayKey(),
        endDate: "",
        scheduledDays: Array.isArray(item.scheduledDays) ? item.scheduledDays : [],
        scheduledTimes: Array.isArray(item.scheduledTimes) && item.scheduledTimes.length ? item.scheduledTimes : ["09:00"],
      })));
      setTemplateDialogVisible(true);
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: "Auto-generate tasks", detail: error?.message || "Unable to load predefined tasks." });
    } finally {
      setLoadingTemplates(false);
    }
  };
  const updateTemplateRow = (index: number, key: string, value: any) => {
    setTemplateRows((current) => current.map((row, rowIndex) => rowIndex === index ? { ...row, [key]: value } : row));
  };
  const addTemplateRow = () => {
    const templateId = `custom-${Date.now()}`;
    setTemplateRows((current) => [...current, {
      templateId,
      title: "",
      instructions: "",
      category: "Other",
      selected: true,
      scope: "facility",
      residentId: "",
      priority: "normal",
      assignedStaffIds: staffOptions[0]?.value ? [staffOptions[0].value] : [],
      scheduleType: "daily",
      scheduledDays: [],
      scheduleMonthDays: [],
      scheduledTimes: ["09:00"],
      startDate: todayKey(),
      endDate: "",
    }]);
    window.setTimeout(() => {
      document.querySelector(`[data-template-row-id="${templateId}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  };
  const createTaskCalendarReminder = async (row: any) => {
    if (!homeId) return;
    const scheduleType = String(row?.scheduleType || "daily").trim().toLowerCase();
    if (scheduleType === "weekly" && (!Array.isArray(row?.scheduledDays) || row.scheduledDays.length === 0)) {
      toast.current?.show({ severity: "warn", summary: "Select a day", detail: "Select at least one day before adding a reminder." });
      return;
    }
    if (scheduleType === "monthly" && (!Array.isArray(row?.scheduleMonthDays) || row.scheduleMonthDays.length === 0)) {
      toast.current?.show({ severity: "warn", summary: "Select a month date", detail: "Select at least one month date before adding a reminder." });
      return;
    }
    const taskId = String(row?.taskId || row?.templateId || row?.title || "");
    setCalendarSavingTaskId(taskId);
    try {
      const reminderTime = row?.scheduledTimes?.[0] || "09:00";
      const lookups = await backendAPIStore.getSharedCalendarLookups();
      const workspaceId = lookups?.workspaceOptions?.[0]?.value;
      if (!workspaceId) throw new Error("No calendar workspace is available for this home.");
      const until = row?.endDate ? combineLosAngelesDateAndTime(String(row.endDate), "23:59") : null;
      const recurrenceRule = row?.scheduleType && row.scheduleType !== "one_time"
        ? { frequency: row.scheduleType === "monthly" ? "monthly" : row.scheduleType === "weekly" ? "weekly" : "daily", interval: 1, until }
        : null;
      const reminderRows = scheduleType === "monthly"
        ? Array.from(new Set(row.scheduleMonthDays.map(Number))).map((monthDay) => ({ ...row, scheduleMonthDays: [monthDay] }))
        : scheduleType === "weekly"
          ? Array.from(new Set(row.scheduledDays.map((day: any) => String(day).toLowerCase()))).map((weekday) => ({ ...row, scheduledDays: [weekday] }))
          : [row];
      const reminderStarts = reminderRows.map((reminderRow: any) => combineLosAngelesDateAndTime(getFutureReminderDateKey(reminderRow, reminderTime), reminderTime));
      if (reminderStarts.some((startAt) => !startAt)) throw new Error("Choose a valid task date and time first.");
      await Promise.all(reminderStarts.map((startAt) => backendAPIStore.createSharedCalendarEvent({
          workspaceId,
          eventType: "general_reminder",
          facilityTaskId: taskId,
          title: String(row?.title || "Facility task"),
          description: String(row?.instructions || ""),
          startAt,
          homeId,
          residentId: row?.residentId || undefined,
          allDay: false,
          notificationSettings: { emailReminder: "none" },
          recurrenceRule,
        })));
      toast.current?.show({ severity: "success", summary: reminderStarts.length === 1 ? "Reminder added" : "Reminders added", detail: `${reminderStarts.length} calendar reminder${reminderStarts.length === 1 ? " was" : "s were"} created.` });
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: "Reminder failed", detail: error?.message || "Unable to create the calendar reminder." });
    } finally {
      setCalendarSavingTaskId("");
    }
  };
  const confirmTaskCalendarReminder = (row: any) => {
    const requestedScheduleType = String(row?.scheduleType || "daily").trim().toLowerCase();
    if (requestedScheduleType === "weekly" && (!Array.isArray(row?.scheduledDays) || row.scheduledDays.length === 0)) {
      toast.current?.show({ severity: "warn", summary: "Select a day", detail: "Select at least one day before adding a reminder." });
      return;
    }
    if (requestedScheduleType === "monthly" && (!Array.isArray(row?.scheduleMonthDays) || row.scheduleMonthDays.length === 0)) {
      toast.current?.show({ severity: "warn", summary: "Select a month date", detail: "Select at least one month date before adding a reminder." });
      return;
    }
    const reminderTime = row?.scheduledTimes?.[0] || "09:00";
    const reminderDate = getFutureReminderDateKey(row, reminderTime);
    const scheduleType = String(row?.scheduleType || "one_time").trim().toLowerCase();
    const scheduledDays = row.scheduledDays.map((day: any) => String(day || "").trim().toUpperCase()).filter(Boolean).join(", ");
    const scheduledMonthDays = (Array.isArray(row?.scheduleMonthDays) ? row.scheduleMonthDays : []).map(Number).filter((day: number) => Number.isInteger(day) && day >= 1 && day <= 31).sort((left: number, right: number) => left - right).join(", ");
    const scheduleLabel = scheduleType === "daily"
      ? "Daily"
      : scheduleType === "weekly"
        ? `Weekly${scheduledDays ? ` on ${scheduledDays}` : ""}`
        : scheduleType === "monthly"
          ? `Monthly${scheduledMonthDays ? ` on ${scheduledMonthDays}` : ""}`
          : "One time";

    confirmDialog({
      header: "Add Calendar Reminder?",
      icon: "pi pi-bell",
      message: <div className="flex flex-column gap-2 mt-2" style={{ minWidth: 0 }}>
        <div style={appMutedTextStyle}>Review the reminder details before adding it to the shared calendar.</div>
        <div className="flex flex-column gap-2 border-1 surface-border border-round p-3" style={{ background: "#f8fafc" }}>
          <div><strong>Task:</strong> {row?.title || "Facility task"}</div>
          <div><strong>Next reminder:</strong> {formatTaskDateTime(reminderDate, reminderTime)} (Pacific Time)</div>
          <div><strong>Repeats:</strong> {scheduleLabel}</div>
          <div><strong>Assignee:</strong> {getAssigneeLabel(row)}</div>
          <div><strong>Scope:</strong> {row?.residentName || "Facility"}</div>
          {row?.instructions ? <div><strong>Instructions:</strong> {row.instructions}</div> : null}
        </div>
      </div>,
      acceptLabel: "Add Reminder",
      rejectLabel: "Cancel",
      ...appConfirmDialogClasses,
      accept: () => void createTaskCalendarReminder(row),
    });
  };
  const saveGeneratedTemplates = async () => {
    if (!homeId) return;
    const selectedRows = templateRows.filter((row) => row.selected && String(row.title || "").trim());
    if (!selectedRows.length) return;
    const rowsWithoutDays = selectedRows.filter((row) => String(row?.scheduleType || "daily").trim().toLowerCase() === "weekly" && (!Array.isArray(row.scheduledDays) || row.scheduledDays.length === 0));
    if (rowsWithoutDays.length) {
      toast.current?.show({ severity: "warn", summary: "Select a day", detail: "Select at least one day for each selected task before saving." });
      return;
    }
    setSavingTemplates(true);
    try {
      await Promise.all(selectedRows.map((row) => backendAPIStore.createFacilityTask({
        homeId,
        title: row.title,
        instructions: row.instructions,
        scope: "facility",
        category: row.category,
        priority: row.priority || "normal",
        assignedStaffIds: row.assignedStaffIds || [],
        scheduleType: row.scheduleType,
        scheduledDays: row.scheduledDays || [],
        scheduledTimes: row.scheduledTimes || [],
        startDate: row.startDate || todayKey(),
        endDate: row.endDate || "",
      })));
      setTemplateDialogVisible(false);
      toast.current?.show({ severity: "success", summary: "Tasks generated", detail: `${selectedRows.length} facility tasks were added.` });
      await load(false);
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: "Auto-generate tasks", detail: error?.message || "Unable to create the selected tasks." });
    } finally {
      setSavingTemplates(false);
    }
  };
  useEffect(() => {
    const handleOpenImport = () => setImportDialogVisible(true);
    const handleOpenAdd = () => openAddTaskDialog();
    const handleOpenExport = () => openExportDialog();
    const handleOpenAutoGenerate = () => void openAutoGenerateDialog();
    window.addEventListener("facility-tasks:open-import", handleOpenImport);
    window.addEventListener("facility-tasks:open-add", handleOpenAdd);
    window.addEventListener("facility-tasks:open-export", handleOpenExport);
    window.addEventListener("facility-tasks:open-auto-generate", handleOpenAutoGenerate);
    return () => {
      window.removeEventListener("facility-tasks:open-import", handleOpenImport);
      window.removeEventListener("facility-tasks:open-add", handleOpenAdd);
      window.removeEventListener("facility-tasks:open-export", handleOpenExport);
      window.removeEventListener("facility-tasks:open-auto-generate", handleOpenAutoGenerate);
    };
  }, [staffOptions]);
  const taskCategoryOptions = useMemo(() => Array.from(new Set([...TASK_CATEGORY_DEFAULTS, ...customCategories.map((category) => category.label), ...tasks.map((item) => String(item.category || "").trim()).filter(Boolean)])).sort().map((value) => ({ label: value, value })), [customCategories, tasks]);
  const visibleTasks = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();
    return tasks.filter((item) => {
      const matchesAssignee = !assigneeFilter || (Array.isArray(item?.assignedStaffIds) && item.assignedStaffIds.some((staffId: any) => String(staffId || "") === assigneeFilter));
      if (!matchesAssignee) return false;
      if (!normalizedSearch) return true;
      return [item.title, item.instructions, item.category, getAssigneeLabel(item)].some((value) => String(value || "").toLowerCase().includes(normalizedSearch));
    }).sort((first, second) => {
      const firstKey = `${taskDateKey(first)}T${String(first?.scheduledTimes?.[0] || "00:00")}`;
      const secondKey = `${taskDateKey(second)}T${String(second?.scheduledTimes?.[0] || "00:00")}`;
      return (firstKey.localeCompare(secondKey) || String(first?.title || "").localeCompare(String(second?.title || ""))) * taskSortOrder;
    });
  }, [assigneeFilter, searchText, taskSortOrder, tasks, staffOptions]);
  const taskStatusSummary = useMemo(() => tasks.reduce((summary: Record<string, number>, task: any) => {
    const status = String(task?.status || "pending").trim().toLowerCase();
    summary[status] = (summary[status] || 0) + 1;
    return summary;
  }, {}), [tasks]);
  const renderTaskListDetails = (row: any) => (
    <div style={{ minWidth: 0, overflowWrap: "anywhere" }}>
      <div className="flex align-items-center gap-2" style={{ fontSize: "0.92rem", fontWeight: 800, lineHeight: 1.3, color: "#0f172a" }}>
        <span style={{ overflowWrap: "anywhere" }}>{row?.title || "Facility Task"}</span>
        {row?.isDeleted ? <Tag value="Deleted" severity="danger" rounded /> : null}
        {hasFullTaskAccess && !row?.isDeleted ? <Button type="button" icon="pi pi-pencil" severity="warning" outlined rounded size="small" style={{ width: "1.8rem", height: "1.8rem", flex: "0 0 auto", color: "#ea580c", background: "#fff7ed" }} disabled={!canEditFacilityTask(row)} tooltip={canEditFacilityTask(row) ? "Edit" : "Future tasks cannot be edited"} aria-label={`Edit ${row?.title || "task"}`} onClick={() => openEditTask(row)} /> : null}
      </div>
      {row?.instructions ? <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.78rem", lineHeight: 1.45, whiteSpace: "pre-wrap" }}>{row.instructions}</div> : null}
      <div className="mt-2 flex flex-column gap-1" style={{ color: "#64748b", fontSize: "0.75rem", lineHeight: 1.45 }}>
        <span><strong>Date &amp; Time:</strong> {formatTaskDateTime(row.occurrenceDate || row.startDate, row.scheduledTimes?.[0])}</span>
        <span><strong>Category:</strong> {row.category || "Other"}</span>
        <span><strong>Assignee:</strong> {getAssigneeLabel(row)}</span>
        <span><strong>Scope:</strong> {row.residentName || "Facility"}</span>
      </div>
    </div>
  );
  const updateTask = (key: string, value: any) => setTask((current: any) => ({ ...current, [key]: value }));

  const saveTaskCategory = async () => {
    const normalized = newCategoryName.trim().slice(0, 80);
    if (!normalized || !homeId) return;
    try {
      const response = editingCategoryId
        ? await backendAPIStore.updateFacilityTaskCategory(homeId, editingCategoryId, normalized)
        : await backendAPIStore.createFacilityTaskCategory(homeId, normalized);
      const savedLabel = String(response?.category?.label || normalized).trim();
      if (Array.isArray(response?.categories)) setCustomCategories(response.categories);
      else if (editingCategoryId) setCustomCategories((current) => current.map((category) => category.categoryId === editingCategoryId ? { ...category, label: savedLabel } : category));
      else setCustomCategories((current) => [...current, { categoryId: String(response?.category?.categoryId || crypto.randomUUID()), label: savedLabel }]);
      updateTask("category", savedLabel);
      setNewCategoryName("");
      setEditingCategoryId("");
      setCategoryDialogVisible(false);
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: "Unable to create category", detail: error?.message || "Please try again." });
    }
  };

  const editTaskCategory = (category: { categoryId: string; label: string }) => {
    setEditingCategoryId(category.categoryId);
    setNewCategoryName(category.label);
  };

  const deleteTaskCategory = (category: { categoryId: string; label: string }) => {
    if (!homeId) return;
    confirmDialog({
      message: renderConfirmMessage("Delete task category?", `"${category.label}" will no longer be available for new tasks.`),
      header: "Delete Confirmation",
      icon: "pi pi-exclamation-triangle",
      ...appConfirmDialogClasses,
      accept: async () => {
        try {
          const response = await backendAPIStore.archiveFacilityTaskCategory(homeId, category.categoryId);
          if (Array.isArray(response?.categories)) setCustomCategories(response.categories);
          else setCustomCategories((current) => current.filter((item) => item.categoryId !== category.categoryId));
        } catch (error: any) {
          toast.current?.show({ severity: "error", summary: "Unable to delete category", detail: error?.message || "Please try again." });
        }
      },
    });
  };

  const saveTask = async () => {
    if (!homeId || !String(task.title || "").trim()) return;
    if (!String(task?.scheduledTimes?.[0] || "").trim()) {
      toast.current?.show({ severity: "warn", summary: "Time required", detail: "Select a scheduled time before saving the task." });
      return;
    }
    if (String(task?.scheduleType || "daily") === "monthly" && (!Array.isArray(task?.scheduleMonthDays) || task.scheduleMonthDays.length === 0)) {
      toast.current?.show({ severity: "warn", summary: "Monthly date required", detail: "Select at least one month date before saving the task." });
      return;
    }
    setSaving(true);
    try {
      if (editingTaskId) {
        const response = await backendAPIStore.updateFacilityTask({ ...task, homeId, taskId: editingTaskId, dateKey });
        const deletedReminderCount = Number(response?.deletedReminderCount || 0);
        toast.current?.show({ severity: "success", summary: "Task updated", detail: deletedReminderCount ? `The facility task was updated and ${deletedReminderCount} existing reminder${deletedReminderCount === 1 ? " was" : "s were"} removed.` : "The facility task was updated." });
      } else {
        await backendAPIStore.createFacilityTask({ ...task, homeId });
        toast.current?.show({ severity: "success", summary: "Task created", detail: "The facility task was scheduled." });
      }
      setDialogVisible(false); setEditingTaskId(null); setTask({ ...EMPTY_TASK, startDate: todayKey() }); await load();
      window.dispatchEvent(new CustomEvent("facility-tasks:refresh"));
    } catch (error: any) { toast.current?.show({ severity: "error", summary: "Facility Tasks", detail: error?.message || "Unable to create task." }); }
    finally { setSaving(false); }
  };

  const openEditTask = (row: any) => {
    setEditingTaskId(row.taskId);
    setTask({ ...EMPTY_TASK, ...row, assignedStaffIds: row.assignedStaffIds?.length ? row.assignedStaffIds : (staffOptions[0]?.value ? [staffOptions[0].value] : []), scheduledDays: row.scheduledDays || [], scheduleMonthDays: row.scheduleMonthDays || [], scheduledTimes: row.scheduledTimes || [] });
    setDialogVisible(true);
  };

  const deleteTask = async (row: any) => {
    if (!homeId) return;
    confirmDialog({
      message: renderConfirmMessage("Delete facility task?", `“${row.title}” will be removed from this facility. All reminders associated with this task will also be deleted.`),
      header: "Delete Confirmation",
      icon: "pi pi-exclamation-triangle",
      ...appConfirmDialogClasses,
      accept: async () => {
        try {
          await backendAPIStore.archiveFacilityTask(homeId, row.taskId);
          setDialogVisible(false);
          setEditingTaskId(null);
          await load();
          window.dispatchEvent(new CustomEvent("facility-tasks:refresh"));
          toast.current?.show({ severity: "success", summary: "Task deleted", detail: "The facility task and its associated reminders were removed." });
        } catch (error: any) {
          toast.current?.show({ severity: "error", summary: "Facility Tasks", detail: error?.message || "Unable to delete task." });
        }
      },
    });
  };

  const updateStatus = async (row: any, status: string) => {
    if (row?.canChart === false) {
      toast.current?.show({ severity: "warn", summary: "Charting unavailable", detail: "This task cannot be charted yet. It becomes available within 1 hour before the scheduled time." });
      return;
    }
    try { await backendAPIStore.updateFacilityTaskStatus({ homeId, taskId: row.taskId, dateKey, status, notes: taskNotes[row.taskId] || row.completionNotes || "" }); await load(); }
    catch (error: any) { toast.current?.show({ severity: "error", summary: "Task update", detail: error?.message || "Unable to update task." }); }
  };

  const importTasks = async (event: any) => {
    const file = event?.files?.[0];
    if (!file || !homeId) return;
    const extension = String(file.name || "").toLowerCase().split(".").pop() || "";
    if (!IMPORT_EXTENSIONS.has(extension)) {
      toast.current?.show({ severity: "error", summary: "Unsupported file", detail: "Use XLS, XLSX, DOCX, or TXT files only." });
      return;
    }
    if (file.size > MAX_IMPORT_FILE_SIZE) {
      toast.current?.show({ severity: "error", summary: "File too large", detail: "Facility task import files must be 1 MB or smaller." });
      return;
    }
    setImporting(true);
    setImportRows([]);
    setImportJob(null);
    setImportPhase("uploading");
    setImportStartedAt(Date.now());
    setImportElapsedSeconds(0);
    try {
      const objectKey = `home/${homeId}/facility-tasks-import/${crypto.randomUUID()}-${file.name}`;
      const uploadUrl = await backendAPIStore.getPresignedUrlForUpload(homeId, ReferenceType.BUSINESS_HOME, objectKey);
      await backendAPIStore.uploadFileToS3(uploadUrl, file);
      setImportPhase("processing");
      const job = await backendAPIStore.startFacilityTaskImportJob({ homeId, objectKey, fileName: file.name });
      let details = await backendAPIStore.getFacilityTaskImportJob(job.jobId);
      const startedAt = Date.now();
      setImportJob(details);
      while (!["COMPLETED", "FAILED"].includes(String(details?.jobStatus || "").toUpperCase()) && Date.now() - startedAt < 300000) {
        await new Promise((resolve) => window.setTimeout(resolve, 3000));
        details = await backendAPIStore.getFacilityTaskImportJob(job.jobId);
        setImportJob(details);
      }
      if (String(details?.jobStatus || "").toUpperCase() !== "COMPLETED") {
        throw new Error(details?.errorMessage || "The task import job did not complete successfully.");
      }
      setImportPhase("completed");
      const generatedRows = Array.isArray(details?.facilityTaskImportPreview?.tasks) ? details.facilityTaskImportPreview.tasks : [];
      setImportRows(generatedRows.map((row: any, index: number) => ({ ...row, importRowId: `${job.jobId}-${index}`, selected: true, residentId: "", startDate: todayKey(), endDate: "", assignedStaffIds: [] })));
      setImportJob(details);
    } catch (error: any) {
      setImportPhase("failed");
      toast.current?.show({ severity: "error", summary: "Import failed", detail: error?.message || "Unable to import facility tasks." });
    } finally { setImporting(false); }
  };

  const closeImportDialog = () => {
    if (importing || saving) return;
    setImportDialogVisible(false);
    setImportJob(null);
    setImportRows([]);
    setImportPhase("idle");
    void load();
  };

  const buildExportPreviewHtml = (dataset: any, view: "list" | "grid") => {
    const activities = Array.isArray(dataset?.activities) ? dataset.activities : [];
    const dates = Array.from({ length: 7 }, (_, index) => {
      const date = new Date(`${dataset?.weekStart}T12:00:00Z`);
      date.setUTCDate(date.getUTCDate() + index);
      return date.toISOString().slice(0, 10);
    });
    const reportTimeToMinutes = (value: any) => {
      const match = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(String(value || "").trim());
      if (!match) return -1;
      let hours = Number(match[1]) % 12;
      if (match[3].toUpperCase() === "PM") hours += 12;
      return hours * 60 + Number(match[2]);
    };
    const times = Array.from(new Set<string>(activities.map((activity: any) => String(activity.time || "")))).filter(Boolean).sort((first, second) => reportTimeToMinutes(first) - reportTimeToMinutes(second) || first.localeCompare(second));
    const listActivities = [...activities].sort((first, second) => first.date.localeCompare(second.date) || reportTimeToMinutes(first.time) - reportTimeToMinutes(second.time));
    const rows = view === "grid"
      ? times.map((time) => `<tr><th class="time">${escapeFacilityTaskExportHtml(time)}</th>${dates.map((date) => `<td>${activities.filter((activity: any) => activity.date === date && activity.time === time).map((activity: any) => `<div class="grid-task"><div class="task-title">${escapeFacilityTaskExportHtml(activity.taskName)}</div>${activity.residentName ? `<div class="task-resident">(${escapeFacilityTaskExportHtml(activity.residentName)})</div>` : ""}</div>`).join("")}</td>`).join("")}</tr>`).join("")
      : listActivities.map((activity: any) => `<tr><td>${escapeFacilityTaskExportHtml(activity.dayLabel)}</td><td>${escapeFacilityTaskExportHtml(activity.time)}</td><td><div class="task-title">${escapeFacilityTaskExportHtml(activity.taskName)}</div>${activity.instructions ? `<div class="task-instructions">${escapeFacilityTaskExportHtml(activity.instructions)}</div>` : ""}</td><td>${escapeFacilityTaskExportHtml(activity.scope || (activity.residentName ? "Resident" : "Facility"))}</td></tr>`).join("");
    const gridHeader = dates.map((date) => `<th>${escapeFacilityTaskExportHtml(new Date(`${date}T12:00:00Z`).toLocaleDateString("en-US", { weekday: "short", timeZone: "UTC" }))}</th>`).join("");
    const emptyRow = view === "grid" ? `<tr><td colspan="8" class="empty">No activities found for this staff member and week.</td></tr>` : `<tr><td colspan="4">No activities found for this staff member and week.</td></tr>`;
    const table = view === "grid" ? `<table class="activities grid"><thead><tr><th>Time</th>${gridHeader}</tr></thead><tbody>${rows || emptyRow}</tbody></table>` : `<table class="activities"><thead><tr><th>Day</th><th>Time</th><th>Task</th><th>Resident</th></tr></thead><tbody>${rows || emptyRow}</tbody></table>`;
    const title = view === "grid" ? "Weekly Staff Activity Grid" : "Weekly Staff Activity Calendar";
    const brandName = String(authExperience.brandName || "AFH Hub");
    const platformDomain = String(authExperience.publicSite.platformDomain || (typeof window !== "undefined" ? window.location.host : "AFHHub.com")).trim();
    const platformUrl = /^https?:\/\//i.test(platformDomain) ? platformDomain : `https://${platformDomain}`;
    return `<!DOCTYPE html><html><head><meta charset="utf-8" /><title>${title}</title><style>*{box-sizing:border-box}body{font-family:Arial,sans-serif;color:#0f172a;margin:0;background:#fff}.page{max-width:1100px;margin:0 auto;padding:28px}h1{text-align:center;margin:0 0 6px;font-size:26px}.subtitle{text-align:center;color:#64748b;margin-bottom:18px}.summary{width:100%;border-collapse:collapse;margin-bottom:18px}.summary td,.summary th,.activities th,.activities td{border:1px solid #cbd5e1;padding:9px 10px;text-align:left;vertical-align:top;overflow-wrap:anywhere;word-break:break-word}.summary th,.activities th{background:#f8fafc;font-weight:700}.activities{width:100%;border-collapse:collapse;font-size:12px}.grid{table-layout:fixed}.grid th,.grid td{width:12.5%;min-height:45px}.grid .time{width:8%;white-space:nowrap}.grid-task + .grid-task{border-top:1px solid #cbd5e1;padding-top:7px;margin-top:7px}.task-title{margin-bottom:4px;overflow-wrap:anywhere;word-break:break-word}.task-resident{color:#64748b;font-size:11px;margin-bottom:6px;overflow-wrap:anywhere;word-break:break-word}.task-instructions{color:#64748b;font-size:11px;white-space:pre-wrap;overflow-wrap:anywhere;word-break:break-word}.empty{text-align:center}.footer{margin-top:24px;text-align:center;color:#64748b;font-size:12px}.footer a{color:#c96a16;text-decoration:none}.footer-separator{color:#64748b;margin:0 4px}.page-number::after{content:counter(page)}@media print{.page{padding:0}}</style></head><body><div class="page"><h1>${title}</h1><div class="subtitle">${escapeFacilityTaskExportHtml(dataset?.weekStart)} - ${escapeFacilityTaskExportHtml(dataset?.weekEnd)}</div><table class="summary"><tr><th>Facility</th><td>${escapeFacilityTaskExportHtml(dataset?.facilityName)}</td><th>Staff</th><td>${escapeFacilityTaskExportHtml(dataset?.staffName)}</td></tr></table>${table}<div class="footer">Generated by ${escapeFacilityTaskExportHtml(brandName)}<span class="footer-separator"> | </span><a href="${escapeFacilityTaskExportHtml(platformUrl)}">${escapeFacilityTaskExportHtml(platformUrl.replace(/^https?:\/\//i, "").replace(/\/$/, ""))}</a><span class="footer-separator"> | </span>Page <span class="page-number"></span></div></div></body></html>`;
  };

  const generateStaffExport = async () => {
    if (!homeId || !exportStaffId) return;
    setExporting(true);
    try {
      const response = await backendAPIStore.exportFacilityTaskWeeklyActivity({ homeId, staffId: exportStaffId, residentId: exportResidentId, weekStart: exportWeekStart, view: exportView });
      setExportDataset(response?.dataset || null);
      setExportDocumentUrl(String(response?.documentUrl || ""));
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: "Export failed", detail: error?.message || "Unable to generate the staff activity report." });
    } finally { setExporting(false); }
  };

  const printStaffExport = () => exportPreviewFrameRef.current?.contentWindow?.print();

  const renderPastEntryTask = (entry: any) => (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontWeight: 700, color: "#0f172a" }}>{entry?.title || "Facility task"}</div>
      {entry?.instructions ? <div className="mt-1" style={{ ...appMutedTextStyle, whiteSpace: "pre-wrap" }}>{entry.instructions}</div> : null}
      <div className="mt-1" style={appMutedTextStyle}>Assignee: {entry?.assignedStaffNames?.join(", ") || "Unassigned"}</div>
      <div className="mt-1" style={appMutedTextStyle}>Scope: {String(entry?.scope || "facility").toLowerCase() === "resident" ? `Resident${entry?.residentName ? ` - ${entry.residentName}` : ""}` : "Facility"}</div>
      <div className="mt-1" style={appMutedTextStyle}>Scheduled: {formatTaskDateTime(entry?.dateKey, entry?.scheduledTimes?.[0])}</div>
    </div>
  );

  const renderPastEntryStatus = (entry: any) => (
    <div style={{ minWidth: 0 }}>
      <Tag value={facilityTaskStatusLabel(entry?.status)} severity={facilityTaskStatusSeverity(entry?.status)} rounded />
      {entry?.completionNotes ? <div className="mt-2" style={{ ...appMutedTextStyle, whiteSpace: "pre-wrap" }}><strong style={{ color: "#334155" }}>Note:</strong> {entry.completionNotes}</div> : null}
      {entry?.isCharted ? <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.8rem" }}>
        {formatTaskLastUpdated(entry?.modifiedAt || entry?.completedAt || entry?.createdAt)}{entry?.modifiedBy || entry?.completedBy ? ` by ${entry.modifiedBy || entry.completedBy}` : ""}
      </div> : <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.8rem" }}>No chart entry was recorded for this scheduled task.</div>}
    </div>
  );

  const renderPastEntryActions = (entry: any) => (
    <div className="flex gap-2 flex-wrap">
      <Button type="button" label={entry?.isCharted ? "Fix" : "Chart"} icon={entry?.isCharted ? "pi pi-pencil" : "pi pi-check-circle"} size="small" severity={entry?.isCharted ? "warning" : "success"} outlined rounded onClick={() => openCorrectionDialog(entry)} />
      {entry?.isCharted ? <Button type="button" label="Audit History" icon="pi pi-history" size="small" severity="secondary" outlined rounded onClick={() => void openEntryHistory(entry)} /> : null}
    </div>
  );

  return <div className="container-fluid p-3 facility-tasks-page">
    <Toast ref={toast} />
    <div style={{ ...appInnerCardStyle, padding: "1rem 1.1rem", marginBottom: "1rem" }}>
      <div className="flex justify-content-between align-items-start gap-3 flex-wrap">
        <div style={{ minWidth: 0 }}>{renderSurfaceHeader("Facility Tasks", "Schedule facility and resident routines for the team.", "pi pi-check-square")}</div>
        <div className="flex align-items-center justify-content-end gap-2 flex-wrap" style={{ marginLeft: "auto" }}>
          <Tag value={`Pending: ${taskStatusSummary.pending || 0}`} severity="warning" rounded />
        </div>
      </div>
    </div>
    <TabView className="resident-workspace-tabs" scrollable activeIndex={activeTabIndex} onTabChange={(event) => setActiveTabIndex(event.index)}>
    <TabPanel header="Today's Pass">
      <StaffFacilityTasksPage assignedToMe={false} showReminder={false} onEditTask={openEditTask} />
    </TabPanel>
    <TabPanel header={`Task List${tasks.length ? ` (${tasks.length})` : ""}`}>
    <div style={{ ...appInnerCardStyle, padding: "1rem 1.1rem" }}>
      {(taskStateCounts.active + taskStateCounts.deleted) > 0 ? <div className="flex justify-content-center align-items-center gap-2 flex-wrap mb-3">
          <Dropdown
            value={taskState}
            options={[
              { label: `Active tasks (${taskStateCounts.active})`, value: "active" },
              { label: `Deleted tasks (${taskStateCounts.deleted})`, value: "deleted" },
            ]}
            optionLabel="label"
            optionValue="value"
            onChange={(event) => { setTaskView("upcoming"); setTaskState(event.value === "deleted" ? "deleted" : "active"); }}
            className="w-14rem"
            aria-label="Filter active or deleted tasks"
          />
          <InputText value={searchText} onChange={(event) => setSearchText(event.target.value)} placeholder="Search" className="w-12rem" aria-label="Search facility tasks" />
          <Dropdown value={assigneeFilter} options={assigneeFilterOptions} optionLabel="label" optionValue="value" onChange={(event) => setAssigneeFilter(event.value || "")} placeholder="All assignees" className="w-12rem" />
          <Calendar value={new Date(`${dateKey}T12:00:00`)} onChange={(event) => event.value && setDateKey(new Intl.DateTimeFormat("en-CA", { timeZone: TIMEZONE }).format(event.value as Date))} dateFormat="yy-mm-dd" readOnlyInput className="w-10rem" aria-label="Task date" />
      </div> : null}
      {loading && !tasks.length ? <div className="flex justify-content-center p-5"><ProgressSpinner /></div> : !tasks.length ? (taskState === "deleted" ? <div className="text-center p-5" style={appMutedTextStyle}>No deleted facility tasks.</div> : <FacilityTasksEmptyState />) : mobile ? <div className="flex flex-column gap-3 mt-3">
        {loading ? <ProgressSpinner className="align-self-center" /> : null}
        {!loading && !visibleTasks.length ? <div className="text-center p-3">No tasks match the current filters.</div> : null}
        {visibleTasks.map((row: any) => <Fragment key={row.taskId}>
          <div className="surface-card border-1 surface-border border-round p-3 w-full">
          {renderTaskListDetails(row)}
          <div className="flex justify-content-end gap-1 mt-3 w-full">
            {row?.isDeleted ? <span style={appMutedTextStyle}>Deleted</span> : <>
            <Button type="button" icon="pi pi-bell" severity="warning" outlined rounded loading={calendarSavingTaskId === String(row.taskId)} tooltip="Add calendar reminder" aria-label={`Set reminder for ${row.title}`} onClick={() => confirmTaskCalendarReminder(row)} />
            {hasFullTaskAccess ? <>{row?.canDelete ? <Button type="button" icon="pi pi-trash" severity="danger" outlined rounded aria-label={`Delete ${row.title}`} onClick={() => void deleteTask(row)} /> : null}<Button type="button" icon="pi pi-copy" severity="secondary" outlined rounded tooltip="Copy task" aria-label={`Copy ${row.title}`} onClick={() => openCopyTask(row)} /></> : null}
            </>}
          </div>
          </div>
        </Fragment>)}
      </div> : <div className="mt-3" style={appDataTableWrapperStyle}><DataTable value={visibleTasks} loading={loading} dataKey="taskId" emptyMessage="No tasks match the current filters." responsiveLayout="scroll" stripedRows size="small" sortField={taskSortField} sortOrder={taskSortOrder} onSort={(event) => { setTaskSortField(String(event.sortField || "time")); setTaskSortOrder(event.sortOrder === -1 ? -1 : 1); }}>
        <Column field="title" header="Task" sortable style={{ width: "68%", minWidth: "24rem" }} bodyStyle={{ verticalAlign: "top" }} body={renderTaskListDetails} />
        <Column header="Actions" style={{ width: "32%", minWidth: "14rem" }} bodyStyle={{ verticalAlign: "top" }} body={(row: any) => row?.isDeleted ? <span style={appMutedTextStyle}>Deleted</span> : hasFullTaskAccess ? <div className="flex gap-2 flex-wrap"><Button type="button" icon="pi pi-bell" severity="warning" outlined rounded loading={calendarSavingTaskId === String(row.taskId)} aria-label={`Set reminder for ${row.title}`} tooltip="Add calendar reminder" onClick={() => confirmTaskCalendarReminder(row)} />{row?.canDelete ? <Button type="button" icon="pi pi-trash" severity="danger" outlined rounded aria-label={`Delete ${row.title}`} tooltip="Delete" onClick={() => void deleteTask(row)} /> : null}<Button type="button" icon="pi pi-copy" severity="secondary" outlined rounded aria-label={`Copy ${row.title}`} tooltip="Copy task" onClick={() => openCopyTask(row)} /></div> : <div className="flex gap-2 align-items-center flex-wrap"><Button type="button" icon="pi pi-bell" severity="warning" outlined rounded loading={calendarSavingTaskId === String(row.taskId)} aria-label={`Set reminder for ${row.title}`} tooltip="Add calendar reminder" onClick={() => confirmTaskCalendarReminder(row)} /><InputText value={taskNotes[row.taskId] ?? row.completionNotes ?? ""} onChange={(event) => setTaskNotes((current) => ({ ...current, [row.taskId]: event.target.value }))} placeholder="Note" /><Button type="button" label="Complete" size="small" severity="warning" rounded disabled={row.status === "completed"} onClick={() => void updateStatus(row, "completed")} /></div>} />
      </DataTable></div>}
      {(hasPreviousTasks || hasMoreTasks) ? <div className="flex justify-content-center align-items-center gap-3 mt-3">
        {hasPreviousTasks ? <Button type="button" label="Load Previous" icon="pi pi-chevron-up" severity="warning" outlined rounded disabled={loading || loadingMoreTasks} onClick={() => setTaskView("previous")} /> : null}
        {hasMoreTasks ? <Button type="button" label="Load More" icon="pi pi-chevron-down" iconPos="right" severity="warning" outlined rounded loading={loadingMoreTasks} disabled={loading} onClick={() => void load(true)} /> : null}
      </div> : null}
    </div>
    </TabPanel>
    {isProvider ? <TabPanel header="Past Entries">
      <div className="facility-task-past-entry-card" style={{ ...appInnerCardStyle, padding: "1rem 1.1rem" }}>
        <div className="facility-task-past-entry-header">
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: "1rem", fontWeight: 800, color: "#0f172a" }}>Past Entries</div>
            <div className="mt-1" style={appMutedTextStyle}>Review and correct charted facility-task entries from the last 90 days. A correction note is required, and every change is retained in audit history.</div>
          </div>
          <div className="facility-task-past-entry-date-filter">
            <label className="block mb-2" htmlFor="facility-task-past-entry-date" style={{ fontSize: "0.78rem", fontWeight: 700, color: "#475569" }}>Date</label>
            <Calendar
              id="facility-task-past-entry-date"
              value={new Date(`${pastEntryDateKey}T12:00:00`)}
              minDate={new Date(`${addLosAngelesDays(todayKey(), -89)}T12:00:00`)}
              maxDate={new Date(`${todayKey()}T12:00:00`)}
              onChange={(event) => event.value instanceof Date && setPastEntryDateKey(dateKeyFromDate(event.value))}
              dateFormat="yy-mm-dd"
              showIcon
              readOnlyInput
              className="w-full"
            />
          </div>
        </div>
        {pastEntriesLoading ? <div className="flex justify-content-center p-5"><ProgressSpinner /></div> : !pastEntries.length ? <div className="text-center p-5" style={appMutedTextStyle}>No charted facility-task entries for this day.</div> : mobile ? <div className="flex flex-column gap-3 mt-3">
          {pastEntries.map((entry) => <div key={`${entry.taskId}-${entry.dateKey}`} className="surface-card border-1 surface-border border-round p-3 w-full">
            {renderPastEntryTask(entry)}
            <div className="mt-3">{renderPastEntryStatus(entry)}</div>
            <div className="mt-3">{renderPastEntryActions(entry)}</div>
          </div>)}
        </div> : <div className="mt-3" style={appDataTableWrapperStyle}>
          <DataTable value={pastEntries} dataKey="taskId" responsiveLayout="scroll" stripedRows size="small" emptyMessage="No charted facility-task entries for this day.">
            <Column header="Task" body={renderPastEntryTask} style={{ width: "45%", minWidth: "22rem" }} bodyStyle={{ verticalAlign: "top" }} />
            <Column header="Status" body={renderPastEntryStatus} style={{ width: "30%", minWidth: "14rem" }} bodyStyle={{ verticalAlign: "top" }} />
            <Column header="Actions" body={renderPastEntryActions} style={{ width: "25%", minWidth: "14rem" }} bodyStyle={{ verticalAlign: "top" }} />
          </DataTable>
        </div>}
      </div>
    </TabPanel> : null}
    </TabView>
    <Dialog
      visible={Boolean(correctionEntry)}
      modal
      header={correctionEntry?.isCharted ? "Fix Chart Entry" : "Chart Past Entry"}
      style={{ width: "min(94vw, 34rem)" }}
      onHide={() => !correctionSaving && setCorrectionEntry(null)}
      footer={<div className="flex justify-content-end gap-2"><Button type="button" label="Cancel" severity="warning" outlined rounded disabled={correctionSaving} onClick={() => setCorrectionEntry(null)} /><Button type="button" label={correctionEntry?.isCharted ? "Save Correction" : "Save Chart"} icon="pi pi-check" severity="warning" rounded loading={correctionSaving} disabled={!correctionStatus || !correctionNote.trim()} onClick={() => void savePastEntryCorrection()} /></div>}
    >
      <div className="flex flex-column gap-3">
        <div style={{ fontWeight: 700, color: "#0f172a" }}>{correctionEntry?.title}</div>
        <div><label className="block mb-2 font-medium" htmlFor="facility-task-correction-status">{correctionEntry?.isCharted ? "Corrected status" : "Status"}</label><Dropdown id="facility-task-correction-status" value={correctionStatus} options={FACILITY_TASK_ENTRY_STATUS_OPTIONS} optionLabel="label" optionValue="value" onChange={(event) => setCorrectionStatus(event.value)} className="w-full" /></div>
        <div><label className="block mb-2 font-medium" htmlFor="facility-task-correction-note">{correctionEntry?.isCharted ? "Correction note" : "Chart note"} <span style={{ color: "#b91c1c" }}>*</span></label><InputTextarea id="facility-task-correction-note" value={correctionNote} onChange={(event) => setCorrectionNote(event.target.value)} rows={3} maxLength={4000} className="w-full" autoResize={false} placeholder={correctionEntry?.isCharted ? "Explain what was corrected and why." : "Document this past task entry."} /></div>
      </div>
    </Dialog>
    <Dialog visible={Boolean(historyEntry)} modal header="Audit History" style={{ width: "min(96vw, 62rem)" }} contentStyle={{ overflowX: "hidden" }} onHide={() => setHistoryEntry(null)}>
      <div className="mb-3">
        <div style={{ fontWeight: 700, color: "#0f172a" }}>{historyEntry?.title}</div>
        <div className="mt-1" style={appMutedTextStyle}>{pastEntryDateKey}</div>
      </div>
      {historyLoading ? <div className="flex justify-content-center p-5"><ProgressSpinner /></div> : <>
        {historyCurrentEntry ? <div className="surface-100 border-round p-3 mb-3"><strong>Current entry:</strong> {facilityTaskStatusLabel(historyCurrentEntry.status)}{historyCurrentEntry.notes ? ` - ${historyCurrentEntry.notes}` : ""}</div> : null}
        {mobile ? (entryHistory.length ? <div className="flex flex-column gap-3">
          {entryHistory.map((row: any, index: number) => <div key={row?.auditId || `${row?.recordedAt || "audit"}-${index}`} style={{ ...appInnerCardStyle, padding: "0.9rem 1rem", width: "100%", maxWidth: "100%", boxSizing: "border-box", overflow: "hidden" }}>
            <div className="flex justify-content-between align-items-start gap-2 flex-wrap">
              <div style={{ fontWeight: 700, color: "#334155", overflowWrap: "anywhere" }}>{formatTaskLastUpdated(row?.recordedAt)}</div>
              <Tag value={String(row?.action || "updated").replace(/_/g, " ")} severity={row?.action === "corrected" ? "warning" : "info"} rounded />
            </div>
            <div className="mt-3 flex flex-column gap-2" style={{ color: "#475569", fontSize: "0.86rem", overflowWrap: "anywhere" }}>
              <div><strong style={{ color: "#334155" }}>Previous status:</strong> {row?.previousStatus ? facilityTaskStatusLabel(row.previousStatus) : "—"}</div>
              <div><strong style={{ color: "#334155" }}>New status:</strong> {facilityTaskStatusLabel(row?.status)}</div>
              <div style={{ whiteSpace: "pre-wrap" }}>
                <strong style={{ color: "#334155" }}>Notes:</strong>
                {row?.correctionNote ? <div className="mt-1">Correction: {row.correctionNote}</div> : null}
                {row?.previousNotes ? <div className="mt-1">Previous: {row.previousNotes}</div> : null}
                {!row?.correctionNote && !row?.previousNotes ? <span> {row?.notes || "—"}</span> : null}
              </div>
              <div><strong style={{ color: "#334155" }}>Changed by:</strong> {row?.recordedBy || "—"}</div>
            </div>
          </div>)}
        </div> : <div className="text-center p-4" style={appMutedTextStyle}>No audit events were captured for this entry.</div>) : <DataTable value={entryHistory} responsiveLayout="scroll" stripedRows size="small" emptyMessage="No audit events were captured for this entry.">
          <Column header="When" body={(row: any) => formatTaskLastUpdated(row?.recordedAt)} style={{ minWidth: "11rem" }} />
          <Column header="Action" body={(row: any) => <Tag value={String(row?.action || "updated").replace(/_/g, " ")} severity={row?.action === "corrected" ? "warning" : "info"} rounded />} style={{ minWidth: "8rem" }} />
          <Column header="Previous" body={(row: any) => row?.previousStatus ? facilityTaskStatusLabel(row.previousStatus) : "—"} style={{ minWidth: "8rem" }} />
          <Column header="New Status" body={(row: any) => facilityTaskStatusLabel(row?.status)} style={{ minWidth: "8rem" }} />
          <Column header="Notes" body={(row: any) => <div style={{ whiteSpace: "pre-wrap" }}>
            {row?.correctionNote ? <div><strong>Correction:</strong> {row.correctionNote}</div> : null}
            {row?.previousNotes ? <div className={row?.correctionNote ? "mt-1" : ""}><strong>Previous:</strong> {row.previousNotes}</div> : null}
            {!row?.correctionNote && !row?.previousNotes ? (row?.notes || "—") : null}
          </div>} style={{ minWidth: "14rem" }} />
          <Column field="recordedBy" header="Changed By" style={{ minWidth: "10rem" }} />
        </DataTable>}
      </>}
    </Dialog>
    <Dialog visible={importDialogVisible} modal header="Import Facility Tasks" style={{ width: "52rem", maxWidth: "96vw" }} onHide={closeImportDialog} footer={<div className="flex justify-content-end gap-2"><Button type="button" label="Close" severity="warning" outlined rounded disabled={importing || saving} onClick={closeImportDialog} /></div>}>
      <div className="flex flex-column gap-3">
        {(importPhase === "idle" || importPhase === "failed") ? <>
          <div style={appMutedTextStyle}>Upload an Excel spreadsheet, Word document, or text file up to 1 MB. We’ll create a task list for you to review before adding it to the facility.</div>
          <FileUpload mode="basic" accept=".xls,.xlsx,.docx,.txt" chooseLabel="Choose file" chooseOptions={{ className: "p-button-warning p-button-outlined p-button-rounded" }} customUpload uploadHandler={importTasks} auto disabled={importing || saving} />
        </> : null}
        {importing || importPhase === "failed" ? <div className="flex align-items-center gap-3 p-3 border-round surface-100">
          <div className="flex flex-column align-items-center text-center gap-2 w-full">
            {importing ? <ProgressSpinner style={{ width: "2rem", height: "2rem" }} strokeWidth="5" /> : null}
            <div>
            <div className="font-semibold">{importPhase === "uploading" ? "Uploading file" : importPhase === "processing" ? String(importJob?.jobSubStatus || "Processing tasks") : importPhase === "completed" ? "Task generation complete" : "Task generation failed"}</div>
            <div style={appMutedTextStyle}>Elapsed time: {importElapsedSeconds}s</div>
            {importJob && importPhase === "processing" ? <div style={appMutedTextStyle}>Status: {String(importJob.jobStatus || "SUBMITTED")}</div> : null}
            </div>
          </div>
        </div> : null}
        {importRows.length ? <><div className="font-semibold">Generated tasks ({importRows.length})</div><DataTable value={importRows} dataKey="importRowId" scrollable scrollHeight="22rem" size="small">
          <Column field="title" header="Task" />
          <Column field="category" header="Category" />
        </DataTable></> : null}
      </div>
    </Dialog>
    <Dialog visible={templateDialogVisible} modal header="Auto-Generate Tasks" style={{ width: "96vw", maxWidth: "1200px" }} contentStyle={{ overflowX: "hidden" }} onHide={() => !savingTemplates && setTemplateDialogVisible(false)} footer={<div className="flex justify-content-between align-items-center gap-2 flex-wrap"><Button type="button" label="Add New Item" icon="pi pi-plus" severity="warning" outlined rounded disabled={savingTemplates} onClick={addTemplateRow} /><div className="flex justify-content-end gap-2"><Button type="button" label="Cancel" severity="warning" outlined rounded disabled={savingTemplates} onClick={() => setTemplateDialogVisible(false)} /><Button type="button" label="Add Selected Tasks" icon="pi pi-check" severity="warning" rounded loading={savingTemplates} disabled={savingTemplates || !templateRows.some((row) => row.selected)} onClick={() => void saveGeneratedTemplates()} /></div></div>}>
      {loadingTemplates ? <div className="flex justify-content-center p-5"><ProgressSpinner /></div> : <>
        <div className="mb-3" style={appMutedTextStyle}>These are pre-populated facility routines. Select the routines to add, then adjust the schedule, time, and assignee before saving.</div>
        <DataTable value={templateRows} dataKey="templateId" responsiveLayout="stack" breakpoint="768px" scrollable scrollHeight="55vh" size="small" emptyMessage="No predefined tasks are available.">
          <Column header="Add" body={(row: any, options: any) => <Checkbox inputId={`facility-template-${row.templateId}`} checked={Boolean(row.selected)} aria-label={`Select ${row.title}`} onChange={(event) => updateTemplateRow(options.rowIndex, "selected", Boolean(event.checked))} />} style={{ width: "3.5rem", minWidth: "3.5rem" }} bodyStyle={{ width: "3.5rem", minWidth: "3.5rem", textAlign: "center" }} />
          <Column header="Task" body={(row: any, options: any) => <div data-template-row-id={row.templateId} className="flex flex-column gap-2" style={{ minWidth: "14rem", overflowWrap: "anywhere" }}><InputText value={row.title} placeholder="Task title" onChange={(event) => updateTemplateRow(options.rowIndex, "title", event.target.value)} /><InputTextarea value={row.instructions} placeholder="Short instructions" rows={2} autoResize onChange={(event) => updateTemplateRow(options.rowIndex, "instructions", event.target.value)} /></div>} />
          <Column header="Days" body={(row: any, options: any) => <MultiSelect value={row.scheduledDays || []} options={DAYS} optionLabel="label" optionValue="value" onChange={(event) => updateTemplateRow(options.rowIndex, "scheduledDays", event.value)} placeholder="Select days" className="w-full" style={{ minWidth: "9rem" }} />} />
          <Column header="Time" body={(row: any, options: any) => <Dropdown value={row.scheduledTimes?.[0] || "09:00"} options={TASK_TIME_OPTIONS} optionLabel="label" optionValue="value" onChange={(event) => updateTemplateRow(options.rowIndex, "scheduledTimes", event.value ? [event.value] : [])} className="w-full" style={{ minWidth: "9rem" }} filter />} />
          <Column header="Assignee" body={(row: any, options: any) => <Dropdown value={row.assignedStaffIds?.[0] || ""} options={staffOptions} optionLabel="label" optionValue="value" onChange={(event) => updateTemplateRow(options.rowIndex, "assignedStaffIds", event.value ? [event.value] : [])} placeholder="Unassigned" showClear className="w-full" style={{ minWidth: "11rem" }} />} />
        </DataTable>
      </>}
    </Dialog>
    <Dialog visible={exportDialogVisible} modal maximizable header="Export Staff Activity" style={{ width: "92vw", maxWidth: "72rem" }} contentStyle={{ maxHeight: "82vh" }} onHide={() => !exporting && setExportDialogVisible(false)} footer={<div className="flex justify-content-end gap-2 flex-wrap"><Button type="button" label="Close" severity="warning" outlined rounded onClick={() => setExportDialogVisible(false)} /><Button type="button" label="Print" icon="pi pi-print" severity="warning" outlined rounded disabled={!exportDataset} onClick={printStaffExport} /><Button type="button" label="Download Word" icon="pi pi-download" severity="warning" rounded disabled={!exportDocumentUrl || exporting} loading={exporting} onClick={() => window.open(exportDocumentUrl, "_blank", "noopener,noreferrer")} /></div>}>
      <div className="flex flex-column gap-3">
        <div className="flex justify-content-center align-items-end flex-wrap gap-2">
          <div><label className="block mb-2 font-medium" htmlFor="export-staff">Staff</label><Dropdown id="export-staff" value={exportStaffId} options={staffOptions} optionLabel="label" optionValue="value" onChange={(event) => setExportStaffId(event.value || "")} placeholder="Select staff" className="w-16rem" /></div>
          <div><label className="block mb-2 font-medium" htmlFor="export-week">Week of</label><Calendar id="export-week" value={new Date(`${exportWeekStart}T12:00:00`)} onChange={(event) => event.value && setExportWeekStart(getWeekStartKey(new Intl.DateTimeFormat("en-CA", { timeZone: TIMEZONE }).format(event.value as Date)))} dateFormat="yy-mm-dd" showIcon readOnlyInput /></div>
          <div><label className="block mb-2 font-medium" htmlFor="export-resident">Resident (optional)</label><Dropdown id="export-resident" value={exportResidentId} options={residentFilterOptions} optionLabel="label" optionValue="value" onChange={(event) => setExportResidentId(event.value || "")} placeholder="All residents" className="w-16rem" /></div>
          <div><label className="block mb-2 font-medium" htmlFor="export-view">Report view</label><Dropdown id="export-view" value={exportView} options={[{ label: "List", value: "list" }, { label: "Grid", value: "grid" }]} optionLabel="label" optionValue="value" onChange={(event) => { setExportView(event.value); setExportDataset(null); setExportDocumentUrl(""); }} className="w-12rem" /></div>
          <Button type="button" label="Generate" icon="pi pi-file-export" severity="warning" rounded disabled={!exportStaffId || exporting} loading={exporting} onClick={() => void generateStaffExport()} />
        </div>
        {exportDataset ? <iframe ref={exportPreviewFrameRef} title="Weekly staff activity preview" srcDoc={buildExportPreviewHtml(exportDataset, exportView)} style={{ width: "100%", height: "58vh", minHeight: "28rem", border: "1px solid #e2e8f0", borderRadius: "12px", background: "#fff" }} /> : <div className="text-center p-5" style={appMutedTextStyle}>Select a staff member, week, and report view to generate the weekly activity calendar.</div>}
      </div>
    </Dialog>
    <Dialog visible={categoryDialogVisible} modal header="Manage Task Categories" style={{ width: "min(94vw, 42rem)" }} onHide={() => setCategoryDialogVisible(false)}>
      <div className="flex flex-column gap-3">
        <div style={{ fontWeight: 700 }}>Custom task categories</div>
        <div style={appMutedTextStyle}>Add categories your facility uses for organizing daily routines and assignments.</div>
        <div className="flex flex-column gap-2">
          <label htmlFor="new-task-category" className="font-medium">Category Name</label>
          <div className="flex gap-2">
            <InputText id="new-task-category" value={newCategoryName} maxLength={80} onChange={(event) => setNewCategoryName(event.target.value)} placeholder="For example, Transportation" className="w-full" autoFocus />
            <Button type="button" label={editingCategoryId ? "Update Category" : "New"} icon="pi pi-plus" severity="warning" outlined rounded onClick={() => void saveTaskCategory()} disabled={!newCategoryName.trim()} />
          </div>
          <small style={{ color: "#64748b" }}>Maximum 80 characters.</small>
        </div>
        {customCategories.length ? <div className="flex flex-column gap-2">{customCategories.map((category) => <div key={category.categoryId} className="flex align-items-center justify-content-between gap-2 p-2" style={{ border: "1px solid #e2e8f0", borderRadius: "10px" }}><div className="flex align-items-center gap-2"><span>{category.label}</span><Tag value="Custom" severity="secondary" /></div><span className="flex gap-1"><Button type="button" icon="pi pi-pencil" text rounded severity="warning" aria-label={`Edit ${category.label}`} onClick={() => editTaskCategory(category)} /><Button type="button" icon="pi pi-trash" text rounded severity="warning" aria-label={`Delete ${category.label}`} tooltip="Delete category" onClick={() => deleteTaskCategory(category)} /></span></div>)}</div> : <div style={appMutedTextStyle}>No custom categories yet.</div>}
      </div>
    </Dialog>
    <Dialog visible={dialogVisible} modal header={editingTaskId ? "Edit Facility Task" : "Add Facility Task"} style={{ width: "52rem", maxWidth: "96vw" }} onHide={() => !saving && setDialogVisible(false)} footer={<div className="flex justify-content-between align-items-center gap-2 w-full">{editingTaskId && task?.canDelete ? <Button type="button" label="Delete Task" icon="pi pi-trash" severity="danger" outlined rounded disabled={saving} onClick={() => void deleteTask(task)} /> : <span />}<div className="flex justify-content-end gap-2"><Button type="button" label="Cancel" severity="warning" outlined rounded disabled={saving} onClick={() => setDialogVisible(false)} /><Button type="button" label={editingTaskId ? "Update Task" : "Save Task"} severity="warning" rounded loading={saving} onClick={() => void saveTask()} /></div></div>}>
      <div className="grid">
        <div className="col-12"><label className="block mb-2 font-medium">Task title</label><InputText value={task.title} onChange={(event) => updateTask("title", event.target.value)} className="w-full" /></div>
        <div className="col-12"><label className="block mb-2 font-medium">Instructions</label><InputTextarea value={task.instructions} onChange={(event) => updateTask("instructions", event.target.value)} rows={2} className="w-full" /></div>
        <div className="col-12 md:col-6"><label className="block mb-2 font-medium">Category</label><div className="flex align-items-center gap-2"><Dropdown value={task.category} options={[...taskCategoryOptions, { label: "Create new category...", value: "__new__" }]} onChange={(event) => { if (event.value === "__new__") { setEditingCategoryId(""); setNewCategoryName(""); setCategoryDialogVisible(true); } else updateTask("category", event.value); }} className="flex-1" filter filterBy="label" /><Button type="button" label="Manage Categories" icon="pi pi-tags" severity="warning" outlined rounded className="flex-shrink-0" aria-label="Manage task categories" onClick={() => { setEditingCategoryId(""); setNewCategoryName(""); setCategoryDialogVisible(true); }} /></div></div>
        <div className="col-12 md:col-6"><label className="block mb-2 font-medium">Scope</label><Dropdown value={task.scope} options={[{ label: "Facility", value: "facility" }, { label: "Resident", value: "resident" }]} onChange={(event) => updateTask("scope", event.value)} className="w-full" /></div>
        {task.scope === "resident" ? <div className="col-12 md:col-6"><label className="block mb-2 font-medium">Resident</label><Dropdown value={task.residentId} options={residentOptions} onChange={(event) => updateTask("residentId", event.value)} className="w-full" /></div> : null}
        <div className="col-12 md:col-6"><label className="block mb-2 font-medium">Assign staff</label><Dropdown value={task.assignedStaffIds?.[0] || ""} options={staffOptions} optionLabel="label" optionValue="value" onChange={(event) => updateTask("assignedStaffIds", event.value ? [event.value] : [])} className="w-full" placeholder="Select staff" showClear /></div>
        <div className="col-12 md:col-6"><label className="block mb-2 font-medium">Schedule</label><Dropdown value={task.scheduleType} options={[{ label: "One time", value: "one_time" }, { label: "Daily", value: "daily" }, { label: "Weekly", value: "weekly" }, { label: "Monthly", value: "monthly" }]} onChange={(event) => setTask((current: any) => ({ ...current, scheduleType: event.value, scheduledDays: event.value === "weekly" ? (current.scheduledDays || []) : [], scheduleMonthDays: event.value === "monthly" ? (current.scheduleMonthDays?.length ? current.scheduleMonthDays : [Number(String(current.startDate || todayKey()).slice(-2)) || 1]) : [] }))} className="w-full" /></div>
        <div className="col-12 p-0"><div className="grid m-0">
          {task.scheduleType === "weekly" ? <div className="col-12 md:col-6"><label className="block mb-2 font-medium">Days</label><MultiSelect value={task.scheduledDays} options={DAYS} onChange={(event) => updateTask("scheduledDays", event.value)} className="w-full" /></div> : null}
          {task.scheduleType === "monthly" ? <div className="col-12 md:col-6"><label className="block mb-2 font-medium">Month dates</label><MultiSelect value={task.scheduleMonthDays || []} options={SCHEDULE_MONTH_DAY_OPTIONS} optionLabel="label" optionValue="value" display="chip" placeholder="Select month dates" selectedItemsLabel="{0} dates selected" showSelectAll={false} onChange={(event) => updateTask("scheduleMonthDays", event.value || [])} className="w-full" /></div> : null}
          <div className="col-12 md:col-6"><label className="block mb-2 font-medium">Time</label><Dropdown value={task.scheduledTimes?.[0] || ""} options={TASK_TIME_OPTIONS} optionLabel="label" optionValue="value" onChange={(event) => updateTask("scheduledTimes", event.value ? [event.value] : [])} className="w-full" placeholder="Select time" filter filterBy="label" showClear /></div>
        </div></div>
        <div className="col-12 md:col-6"><label className="block mb-2 font-medium" htmlFor="facility-task-start-date">Start date</label><Calendar id="facility-task-start-date" value={task.startDate ? new Date(`${task.startDate}T12:00:00`) : null} onChange={(event) => updateTask("startDate", event.value ? dateKeyFromDate(event.value as Date) : "")} className="w-full" dateFormat="yy-mm-dd" showIcon readOnlyInput /></div>
      </div>
    </Dialog>
    {authStore.isAdmin() && homeId ? <AdminResetControl label="Reset Facility Tasks" description="Delete every facility task, category, and task status history for this home." onReset={resetTasks} /> : null}
    <style>
      {`
        .facility-tasks-page .resident-workspace-tabs {
          width: 100%;
        }

        .facility-tasks-page .resident-workspace-tabs .p-tabview-nav-container {
          margin-bottom: 1rem;
        }

        .facility-tasks-page .resident-workspace-tabs .p-tabview-nav {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 0.55rem;
          padding: 0.45rem;
          border: 1px solid #dbe4f0;
          border-radius: 18px;
          background: linear-gradient(180deg, #f8fbff 0%, #f1f5f9 100%);
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8);
          width: 100%;
        }

        .facility-tasks-page .resident-workspace-tabs .p-tabview-nav li {
          margin: 0;
        }

        .facility-tasks-page .resident-workspace-tabs .p-tabview-nav li .p-tabview-nav-link {
          border: 1px solid #d7e0ea;
          border-radius: 14px;
          background: #ffffff;
          color: #475569;
          padding: 0.8rem 1rem;
          font-weight: 700;
          transition: all 0.18s ease;
          box-shadow: 0 1px 1px rgba(15, 23, 42, 0.04);
        }

        .facility-tasks-page .resident-workspace-tabs .p-tabview-nav li .p-tabview-nav-link:not(.p-disabled):focus {
          box-shadow: 0 0 0 0.18rem rgba(59, 130, 246, 0.16);
        }

        .facility-tasks-page .resident-workspace-tabs .p-tabview-nav li:not(.p-highlight) .p-tabview-nav-link:hover {
          border-color: #bfdbfe;
          color: #1d4ed8;
          transform: translateY(-1px);
        }

        .facility-tasks-page .resident-workspace-tabs .p-tabview-nav li.p-highlight .p-tabview-nav-link {
          border-color: #fdba74;
          background: linear-gradient(135deg, #fff7ed 0%, #ffedd5 55%, #fef3c7 100%);
          color: #0f172a;
          box-shadow: 0 8px 18px rgba(249, 115, 22, 0.14);
        }

        .facility-tasks-page .resident-workspace-tabs .p-tabview-panels {
          padding: 0;
          background: transparent;
        }

        .facility-tasks-page .resident-workspace-tabs .p-tabview-panels .p-tabview-panel {
          display: flex;
          flex-direction: column;
          align-items: stretch;
        }

        .facility-tasks-page .resident-workspace-tabs .p-tabview-panels .p-tabview-panel > * {
          width: 100%;
        }

        .facility-task-past-entry-date-filter {
          width: 100%;
          max-width: 100%;
          min-width: 0;
          justify-self: end;
        }

        .facility-task-past-entry-card {
          box-sizing: border-box;
          width: 100%;
          max-width: 100%;
          overflow: hidden;
        }

        .facility-task-past-entry-header {
          display: grid;
          grid-template-columns: minmax(0, 60%) minmax(0, 30%);
          align-items: start;
          column-gap: 10%;
          row-gap: 1rem;
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
        }

        .facility-task-past-entry-date-filter .p-calendar {
          display: flex;
          width: 100%;
          max-width: 100%;
          min-width: 0;
        }

        .facility-task-past-entry-date-filter .p-inputtext {
          flex: 1 1 auto;
          width: 1%;
          min-width: 0;
        }

      `}
    </style>
  </div>;
};

export default FacilityTasksPage;
